<template>
  <div class="admin-user-page">
    <div class="page-container">
      <button @click="router.back()" class="back-button">← Back to Search</button>

      <div v-if="loading" class="loading-state">
        <div class="spinner"></div>
      </div>

      <div v-else-if="error" class="error-state">{{ error }}</div>

      <div v-else-if="userData">
        <header class="page-header">
          <div class="user-header">
            <div>
              <h1 class="page-title">{{ userData.user.name || 'No Name' }}</h1>
              <p class="user-email">{{ userData.user.email }}</p>
            </div>
            <div class="actions-dropdown" ref="actionsContainer">
              <button @click="toggleActionsMenu" class="actions-button">
                Actions
                <svg class="chevron" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fill-rule="evenodd"
                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                    clip-rule="evenodd"
                  />
                </svg>
              </button>

              <div v-if="showActionsMenu" class="actions-menu" ref="actionsMenu">
                <button @click="handleImpersonate" class="menu-item">Impersonate Dev Edits</button>
                <button v-if="hasArcs" @click="handleImpersonateArcs" class="menu-item">Impersonate Arcs</button>
                <button
                  v-if="arcs && !arcs.admin && !userData.user.deletedAt"
                  @click="handleArcsAccess(!arcs.access)"
                  class="menu-item"
                >
                  {{ arcs.access ? 'Revoke Arcs access' : 'Grant Arcs access' }}
                </button>
                <button v-if="!userData.user.deletedAt" @click="handleGrantCredits" class="menu-item">
                  Grant Credits
                </button>
                <button v-if="!userData.user.deletedAt" @click="handleSetAdmin(!userData.user.admin)" class="menu-item">
                  {{ userData.user.admin ? 'Remove Admin' : 'Make Admin' }}
                </button>
                <button v-if="userData.user.deletedAt" @click="handleRestore" class="menu-item">Undelete User</button>
                <button v-else @click="handleDelete" class="menu-item danger">Delete User</button>
              </div>
            </div>
          </div>
        </header>

        <div v-if="userData.user.deletedAt" class="deletion-warning">
          Deleting in {{ daysUntilPermanentDelete }} days
        </div>

        <div class="info-grid">
          <div class="info-card">
            <h3 class="info-title">User Details</h3>
            <dl class="info-list">
              <div class="info-item">
                <dt>Email</dt>
                <dd v-if="!editingEmail" class="email-dd">
                  {{ userData.user.email }}
                  <button @click="startEditEmail" class="edit-email-button" title="Edit email">✎</button>
                </dd>
                <dd v-else class="email-edit">
                  <input
                    v-model="emailDraft"
                    type="email"
                    class="email-input"
                    :disabled="savingEmail"
                    @keydown.enter="saveEmail"
                    @keydown.escape="cancelEditEmail"
                    autofocus
                  />
                  <button @click="saveEmail" class="email-save" :disabled="savingEmail || !emailDraft">Save</button>
                  <button @click="cancelEditEmail" class="email-cancel" :disabled="savingEmail">Cancel</button>
                  <span v-if="emailError" class="email-error">{{ emailError }}</span>
                </dd>
              </div>
              <div class="info-item">
                <dt>Name</dt>
                <dd>{{ userData.user.name || '—' }}</dd>
              </div>
              <div class="info-item">
                <dt>Admin</dt>
                <dd>{{ userData.user.admin ? 'Yes' : 'No' }}</dd>
              </div>
              <div class="info-item">
                <dt>Last Seen</dt>
                <dd>{{ formatDate(userData.user.lastSeenAt) }}</dd>
              </div>
              <div class="info-item">
                <dt>Created</dt>
                <dd>{{ formatDate(userData.user.createdAt) }}</dd>
              </div>
              <div v-if="userData.user.deletedAt" class="info-item">
                <dt>Deleted</dt>
                <dd>{{ formatDate(userData.user.deletedAt) }}</dd>
              </div>
              <div class="info-item">
                <dt>Stripe Customer</dt>
                <dd v-if="userData.user.stripeId">
                  <a
                    :href="`https://dashboard.stripe.com/customers/${userData.user.stripeId}`"
                    target="_blank"
                    class="stripe-link"
                  >
                    {{ userData.user.stripeId }}
                  </a>
                </dd>
                <dd v-else>—</dd>
              </div>
            </dl>
          </div>

          <div class="info-card">
            <div class="credits-header">
              <h3 class="info-title">Credits</h3>
              <span class="credit-balance">{{ userData.creditBalance ?? 0 }}</span>
            </div>
            <div v-if="!userData.creditTransactions || userData.creditTransactions.length === 0" class="empty-projects">
              No credit activity
            </div>
            <ul v-else class="transactions-list">
              <li v-for="tx in userData.creditTransactions" :key="tx.id" class="transaction-item">
                <div class="transaction-main">
                  <span class="transaction-type" :class="`type-${tx.type}`">{{ formatTxType(tx.type) }}</span>
                  <span class="transaction-amount" :class="tx.amount > 0 ? 'positive' : 'negative'">
                    {{ tx.amount > 0 ? '+' : '' }}{{ tx.amount }}
                  </span>
                </div>
                <div class="transaction-meta">
                  <span>{{ formatDate(tx.createdAt) }}</span>
                  <span v-if="txReason(tx)" class="transaction-reason">· {{ txReason(tx) }}</span>
                </div>
              </li>
            </ul>
          </div>

          <div class="info-card">
            <h3 class="info-title">Edits ({{ userData.totalProjects }})</h3>
            <div v-if="userData.projects.length === 0" class="empty-projects">No edits</div>
            <ul v-else class="projects-list">
              <li
                v-for="project in userData.projects"
                :key="project.id"
                class="project-item"
                :class="{ 'is-deleted': project.deletedAt }"
              >
                <a :href="`/edit/${project.id}`" target="_blank" class="project-link">
                  {{ project.title || 'Untitled' }}
                </a>
                <span v-if="project.deletedAt" class="project-deleted-badge">Deleted</span>
                <span class="project-date">{{ formatDate(project.createdAt) }}</span>
                <button
                  v-if="project.deletedAt"
                  class="project-action restore"
                  @click="handleRestoreProject(project.id)"
                >
                  Restore
                </button>
                <button v-else class="project-action delete" @click="handleDeleteProject(project)">Delete</button>
              </li>
            </ul>
            <p v-if="userData.totalProjects > 10" class="more-projects">and {{ userData.totalProjects - 10 }} more</p>
          </div>

          <div class="info-card">
            <div class="credits-header">
              <h3 class="info-title">Arcs</h3>
              <span v-if="arcs" class="arcs-role" :class="{ 'has-access': arcs.admin || arcs.access }">{{
                arcsRole
              }}</span>
            </div>
            <div v-if="arcsUnavailable" class="empty-projects">Arcs isn't reachable right now.</div>
            <div v-else-if="!arcs" class="empty-projects">Loading…</div>
            <div v-else-if="!hasArcs" class="empty-projects">
              Hasn't used Arcs.
              <button v-if="!userData.user.deletedAt" class="project-action restore" @click="handleArcsAccess(true)">
                Grant access
              </button>
            </div>
            <div v-else-if="arcs.boards.length === 0" class="empty-projects">No boards</div>
            <ul v-else class="projects-list">
              <li
                v-for="board in arcs.boards"
                :key="board.id"
                class="project-item"
                :class="{ 'is-deleted': board.archivedAt }"
              >
                <a :href="arcsBoardLink(board.id)" target="_blank" class="project-link">
                  {{ board.title || 'Untitled' }}
                  <span v-if="board.draftLabel" class="arcs-draft">{{ board.draftLabel }}</span>
                </a>
                <span v-if="board.archivedAt" class="project-deleted-badge">Archived</span>
                <span class="project-date">{{ formatDate(new Date(board.updatedAt).toISOString()) }}</span>
              </li>
            </ul>
            <p v-if="hasArcs" class="arcs-hint">
              Board links open read-only, viewing as {{ userData.user.name || 'this user' }}.
            </p>
          </div>
        </div>
      </div>
    </div>

    <transition name="modal">
      <div v-if="showGrantModal" class="modal-overlay" @click="closeGrantModal">
        <div class="modal-content" @click.stop>
          <div class="modal-header">
            <h3>Grant Credits</h3>
            <button @click="closeGrantModal" class="modal-close" aria-label="Close">×</button>
          </div>
          <form @submit.prevent="submitGrantCredits">
            <div class="modal-body">
              <p class="modal-subtitle">
                Granting to <strong>{{ userData?.user.email }}</strong>
              </p>

              <label class="field-label" for="grant-credits-amount">Credits</label>
              <input
                id="grant-credits-amount"
                v-model.number="grantAmount"
                type="number"
                min="1"
                max="100"
                step="1"
                required
                class="field-input"
                :disabled="granting"
                autofocus
              />
              <p class="field-hint">Between 1 and 100.</p>

              <label class="field-label" for="grant-credits-reason">Reason (optional)</label>
              <input
                id="grant-credits-reason"
                v-model="grantReason"
                type="text"
                placeholder="e.g. partner batch, support credit"
                class="field-input"
                :disabled="granting"
                maxlength="200"
              />

              <p v-if="grantError" class="grant-error">{{ grantError }}</p>
            </div>
            <div class="modal-footer">
              <button type="button" @click="closeGrantModal" class="modal-cancel" :disabled="granting">Cancel</button>
              <button type="submit" class="modal-submit" :disabled="granting || !isGrantValid">
                {{ granting ? 'Granting…' : `Grant ${grantAmount || ''} ${grantAmount === 1 ? 'credit' : 'credits'}` }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { deleteProject, restoreProject } from '../services/api';
import { useAuthStore } from '../stores/auth';

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();

const userData = ref<any>(null);
const loading = ref(true);
const error = ref('');
const showActionsMenu = ref(false);
const actionsContainer = ref<HTMLDivElement>();
const actionsMenu = ref<HTMLDivElement>();

const showGrantModal = ref(false);
const grantAmount = ref<number | ''>(1);
const grantReason = ref('');
const granting = ref(false);
const grantError = ref('');

const editingEmail = ref(false);
const emailDraft = ref('');
const savingEmail = ref(false);
const emailError = ref('');

// Arcs (arcs.twig.io) shares the Twig login. Its admin routes accept this page's ID token directly.
const ARCS_URL = ['localhost', '127.0.0.1'].includes(window.location.hostname)
  ? 'http://localhost:5173'
  : 'https://arcs.twig.io';

interface ArcsPerson {
  uid: string;
  email: string;
  name: string;
  admin: boolean;
  access: { role: 'admin' | 'tester' | 'member'; grantedAt: number; grantedBy: string | null } | null;
  boards: { id: string; title: string; draftLabel: string | null; updatedAt: number; archivedAt: number | null }[];
}

const arcs = ref<ArcsPerson | null>(null);
const arcsUnavailable = ref(false);

const hasArcs = computed(
  () => !!arcs.value && (arcs.value.admin || !!arcs.value.access || arcs.value.boards.length > 0)
);

const arcsRole = computed(() => {
  if (!arcs.value) return '';
  if (arcs.value.admin) return 'Admin';
  if (arcs.value.access) {
    const role = arcs.value.access.role;
    return `${role.charAt(0).toUpperCase()}${role.slice(1)} since ${formatDate(new Date(arcs.value.access.grantedAt).toISOString())}`;
  }
  return 'No access';
});

async function loadArcs() {
  arcsUnavailable.value = false;
  arcs.value = null;
  const uid = route.params['uid'] as string;
  try {
    const token = await authStore.getIdToken();
    const response = await fetch(`${ARCS_URL}/api/admin/people/${encodeURIComponent(uid)}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (response.status === 404) {
      arcs.value = { uid, email: '', name: '', admin: false, access: null, boards: [] };
      return;
    }
    if (!response.ok) throw new Error(`Arcs answered ${response.status}`);
    arcs.value = await response.json();
  } catch (err) {
    console.error('Failed to load Arcs details:', err);
    arcsUnavailable.value = true;
  }
}

function arcsBoardLink(boardId: string): string {
  return `${ARCS_URL}/b/${boardId}?viewAs=${encodeURIComponent(userData.value.user.uid)}`;
}

function handleImpersonateArcs() {
  window.open(`${ARCS_URL}/?viewAs=${encodeURIComponent(userData.value.user.uid)}`, '_blank');
  showActionsMenu.value = false;
}

async function handleArcsAccess(grant: boolean) {
  showActionsMenu.value = false;
  const email = userData.value.user.email;
  if (
    !confirm(
      grant ? `Give ${email} access to Arcs (as a tester)?` : `Take away ${email}'s access to Arcs? Their boards stay.`
    )
  ) {
    return;
  }
  try {
    const token = await authStore.getIdToken();
    const uid = userData.value.user.uid;
    const response = await fetch(
      grant ? `${ARCS_URL}/api/admin/access` : `${ARCS_URL}/api/admin/access/${encodeURIComponent(uid)}`,
      {
        method: grant ? 'POST' : 'DELETE',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: grant ? JSON.stringify({ uid, role: 'tester' }) : null,
      }
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Unknown error' }));
      alert(err.error || 'Failed to update Arcs access');
    }
    await loadArcs();
  } catch (err) {
    console.error('Arcs access update failed:', err);
    alert('Failed to update Arcs access');
  }
}

function startEditEmail() {
  emailDraft.value = userData.value.user.email;
  emailError.value = '';
  editingEmail.value = true;
}

function cancelEditEmail() {
  editingEmail.value = false;
  emailError.value = '';
}

async function saveEmail() {
  const newEmail = emailDraft.value.trim();
  if (!newEmail || newEmail === userData.value.user.email) {
    cancelEditEmail();
    return;
  }

  savingEmail.value = true;
  emailError.value = '';

  try {
    const token = await authStore.getIdToken();
    const response = await fetch(`/api/users/${userData.value.user.uid}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ email: newEmail }),
    });

    if (response.ok) {
      editingEmail.value = false;
      await loadUser();
    } else {
      const err = await response.json().catch(() => ({ error: 'Unknown error' }));
      emailError.value = err.error || 'Failed to update email';
    }
  } catch {
    emailError.value = 'Network error. Try again.';
  } finally {
    savingEmail.value = false;
  }
}

const isGrantValid = computed(() => {
  const n = Number(grantAmount.value);
  return Number.isInteger(n) && n >= 1 && n <= 100;
});

const daysUntilPermanentDelete = computed(() => {
  if (!userData.value?.user.deletedAt) return 0;
  const deletedDate = new Date(userData.value.user.deletedAt);
  const deleteDate = new Date(deletedDate.getTime() + 30 * 24 * 60 * 60 * 1000);
  const now = new Date();
  const diffMs = deleteDate.getTime() - now.getTime();
  return Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
});

async function loadUser() {
  loading.value = true;
  error.value = '';

  try {
    const uid = route.params['uid'] as string;
    const token = await authStore.getIdToken();

    const response = await fetch(`/api/admin/users/${uid}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to load user');
    }

    userData.value = await response.json();
  } catch (err) {
    error.value = 'Failed to load user details';
    console.error(err);
  } finally {
    loading.value = false;
  }
}

async function handleDeleteProject(project: { id: string; title?: string | null }) {
  if (!window.confirm(`Delete "${project.title || 'Untitled'}"? It will be permanently purged after 30 days.`)) {
    return;
  }
  try {
    await deleteProject(project.id);
    await loadUser();
  } catch (err) {
    console.error('Failed to delete project:', err);
    window.alert('Failed to delete project. Try again.');
  }
}

async function handleRestoreProject(projectId: string) {
  try {
    await restoreProject(projectId);
    await loadUser();
  } catch (err) {
    console.error('Failed to restore project:', err);
    window.alert('Failed to restore project. Try again.');
  }
}

function toggleActionsMenu() {
  showActionsMenu.value = !showActionsMenu.value;
}

async function handleImpersonate() {
  try {
    window.open(`/edits?impersonate=${userData.value.user.uid}`, '_blank');
  } catch (error) {
    console.error('Impersonation failed:', error);
    alert('Failed to impersonate user');
  }
  showActionsMenu.value = false;
}

async function handleSetAdmin(setAdmin: boolean) {
  const confirmMsg = setAdmin
    ? 'Grant admin privileges to this user? They will need to sign out and back in.'
    : 'Remove admin privileges from this user? They will need to sign out and back in.';

  if (!confirm(confirmMsg)) return;

  try {
    const token = await authStore.getIdToken();

    const response = await fetch('/api/admin/set-admin', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ uid: userData.value.user.uid, isAdmin: setAdmin }),
    });

    if (response.ok) {
      alert(`Admin status ${setAdmin ? 'granted' : 'removed'}. User must sign out and back in.`);
      await loadUser();
    } else {
      alert('Failed to update admin status');
    }
  } catch (error) {
    console.error('Set admin failed:', error);
    alert('Failed to update admin status');
  }
  showActionsMenu.value = false;
}

async function handleDelete() {
  if (!confirm('Delete this user? They can be restored within 30 days.')) return;

  try {
    const token = await authStore.getIdToken();

    const response = await fetch(`/api/users/${userData.value.user.uid}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.ok) {
      await loadUser();
    } else {
      alert('Failed to delete user');
    }
  } catch (error) {
    console.error('Delete failed:', error);
    alert('Failed to delete user');
  }
  showActionsMenu.value = false;
}

function handleGrantCredits() {
  showActionsMenu.value = false;
  grantAmount.value = 1;
  grantReason.value = '';
  grantError.value = '';
  showGrantModal.value = true;
}

function closeGrantModal() {
  if (granting.value) return;
  showGrantModal.value = false;
}

async function submitGrantCredits() {
  if (!isGrantValid.value) {
    grantError.value = 'Enter a whole number between 1 and 100.';
    return;
  }
  const credits = Number(grantAmount.value);
  const reason = grantReason.value.trim();

  granting.value = true;
  grantError.value = '';

  try {
    const token = await authStore.getIdToken();
    const response = await fetch(`/api/admin/users/${userData.value.user.uid}/grant-credits`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ credits, reason: reason || undefined }),
    });

    if (response.ok) {
      showGrantModal.value = false;
      await loadUser();
    } else {
      const err = await response.json().catch(() => ({ error: 'Unknown error' }));
      grantError.value = err.error || `Failed: ${response.statusText}`;
    }
  } catch (err) {
    console.error('Grant credits failed:', err);
    grantError.value = 'Network error. Try again.';
  } finally {
    granting.value = false;
  }
}

function formatTxType(type: string): string {
  switch (type) {
    case 'purchase':
      return 'Purchase';
    case 'deduction':
      return 'Used';
    case 'admin_grant':
      return 'Admin grant';
    case 'refund':
      return 'Refund';
    case 'expiration':
      return 'Expired';
    default:
      return type;
  }
}

function txReason(tx: { metadata?: string | null }): string | null {
  if (!tx.metadata) return null;
  try {
    const parsed = JSON.parse(tx.metadata);
    if (parsed.reason) return parsed.reason;
    if (parsed.grantedBy) return `granted by ${parsed.grantedBy}`;
    return null;
  } catch {
    return null;
  }
}

async function handleRestore() {
  if (!confirm('Restore this user?')) return;

  try {
    const token = await authStore.getIdToken();

    const response = await fetch(`/api/admin/users/${userData.value.user.uid}/restore`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (response.ok) {
      await loadUser();
    } else {
      alert('Failed to restore user');
    }
  } catch (error) {
    console.error('Restore failed:', error);
    alert('Failed to restore user');
  }
  showActionsMenu.value = false;
}

function formatDate(date: string | null): string {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function handleClickOutside(event: MouseEvent) {
  if (showActionsMenu.value && actionsContainer.value && !actionsContainer.value.contains(event.target as Node)) {
    showActionsMenu.value = false;
  }
}

onMounted(() => {
  loadUser();
  loadArcs();
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<style scoped>
.admin-user-page {
  flex: 1;
  background: #f7fafc;
}

.page-container {
  max-width: 1000px;
  margin: 0 auto;
  padding: 3rem 1.5rem;
}

.back-button {
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  color: #4a5568;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  cursor: pointer;
  margin-bottom: 2rem;
  font-family: inherit;
}

.back-button:hover {
  background: #f7fafc;
}

.page-header {
  margin-bottom: 2rem;
}

.user-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}

.page-title {
  font-size: 2.25rem;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 0.25rem;
}

.user-email {
  font-size: 1rem;
  color: #718096;
}

.actions-dropdown {
  position: relative;
}

.actions-button {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: white;
  background: var(--color-accent);
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-family: inherit;
}

.chevron {
  width: 16px;
  height: 16px;
}

.actions-menu {
  position: absolute;
  right: 0;
  top: calc(100% + 0.5rem);
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  min-width: 200px;
  z-index: 10;
}

.menu-item {
  display: block;
  width: 100%;
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  color: #4a5568;
  background: none;
  border: none;
  text-align: left;
  cursor: pointer;
  font-family: inherit;
}

.menu-item:hover {
  background: #f7fafc;
}

.menu-item.danger {
  color: #e53e3e;
}

.deletion-warning {
  background: #fff5f5;
  border: 1px solid #feb2b2;
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 2rem;
  color: #742a2a;
  font-weight: 600;
  text-align: center;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 1.5rem;
}

.info-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 1.5rem;
}

.info-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1a202c;
  margin-bottom: 1rem;
}

.info-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 1rem;
}

.info-item dt {
  font-weight: 600;
  color: #718096;
  font-size: 0.875rem;
}

.info-item dd {
  color: #1a202c;
  font-size: 0.875rem;
  text-align: right;
}

.email-dd {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: flex-end;
}

.edit-email-button {
  background: none;
  border: none;
  cursor: pointer;
  color: #a0aec0;
  font-size: 0.875rem;
  padding: 0 0.25rem;
  line-height: 1;
  font-family: inherit;
}

.edit-email-button:hover {
  color: var(--color-accent);
}

.email-edit {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.email-input {
  padding: 0.25rem 0.5rem;
  font-size: 0.875rem;
  font-family: inherit;
  border: 1px solid #cbd5e0;
  border-radius: 4px;
  width: 200px;
}

.email-input:focus {
  outline: none;
  border-color: var(--color-accent);
}

.email-save,
.email-cancel {
  padding: 0.25rem 0.625rem;
  font-size: 0.75rem;
  font-weight: 600;
  border-radius: 4px;
  cursor: pointer;
  font-family: inherit;
  border: none;
}

.email-save {
  background: var(--color-accent);
  color: white;
}

.email-save:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.email-cancel {
  background: #edf2f7;
  color: #4a5568;
}

.email-error {
  width: 100%;
  font-size: 0.75rem;
  color: #e53e3e;
  text-align: right;
}

.stripe-link {
  color: var(--color-accent);
  text-decoration: none;
}

.stripe-link:hover {
  text-decoration: underline;
}

.projects-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.project-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 0;
  border-bottom: 1px solid #e2e8f0;
}

.project-item:last-child {
  border-bottom: none;
}

.project-item.is-deleted .project-link {
  opacity: 0.6;
}

.project-link {
  flex: 1;
  min-width: 0;
  color: var(--color-accent);
  text-decoration: none;
  font-size: 0.875rem;
}

.project-link:hover {
  text-decoration: underline;
}

.project-date {
  color: #718096;
  font-size: 0.75rem;
}

.project-deleted-badge {
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 0.125rem 0.5rem;
  border-radius: 10px;
  background: #fed7d7;
  color: #742a2a;
}

.project-action {
  padding: 0.25rem 0.625rem;
  font-size: 0.75rem;
  font-weight: 600;
  border: 1px solid transparent;
  border-radius: 6px;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.15s;
}

.project-action.delete {
  color: #e53e3e;
  background: #fff5f5;
  border-color: #fed7d7;
}

.project-action.delete:hover {
  background: #fed7d7;
}

.project-action.restore {
  color: #2a4365;
  background: #ebf8ff;
  border-color: #bee3f8;
}

.project-action.restore:hover {
  background: #bee3f8;
}

.empty-projects {
  color: #718096;
  font-size: 0.875rem;
  text-align: center;
  padding: 2rem 0;
}

.credits-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
}

.credits-header .info-title {
  margin-bottom: 0;
}

.credit-balance {
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--color-accent);
}

.transactions-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.transaction-item {
  padding: 0.5rem 0;
  border-bottom: 1px solid #e2e8f0;
}

.transaction-item:last-child {
  border-bottom: none;
}

.transaction-main {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.transaction-type {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.125rem 0.5rem;
  border-radius: 10px;
  background: #edf2f7;
  color: #4a5568;
}

.transaction-type.type-admin_grant {
  background: #fefcbf;
  color: #744210;
}

.transaction-type.type-purchase {
  background: #c6f6d5;
  color: #22543d;
}

.transaction-type.type-deduction {
  background: #fed7d7;
  color: #742a2a;
}

.transaction-amount {
  font-weight: 600;
  font-size: 0.875rem;
}

.transaction-amount.positive {
  color: #22543d;
}

.transaction-amount.negative {
  color: #742a2a;
}

.transaction-meta {
  font-size: 0.75rem;
  color: #718096;
  margin-top: 0.25rem;
}

.transaction-reason {
  margin-left: 0.25rem;
}

/* Grant Credits Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  z-index: 100;
}

.modal-content {
  background: white;
  border-radius: 12px;
  max-width: 460px;
  width: 100%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e2e8f0;
}

.modal-header h3 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1a202c;
  margin: 0;
}

.modal-close {
  background: none;
  border: none;
  font-size: 1.75rem;
  color: #a0aec0;
  cursor: pointer;
  line-height: 1;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  transition: all 0.15s;
}

.modal-close:hover {
  background: #f7fafc;
  color: #4a5568;
}

.modal-body {
  padding: 1.5rem;
}

.modal-subtitle {
  color: #4a5568;
  font-size: 0.875rem;
  margin: 0 0 1.25rem;
}

.modal-subtitle strong {
  color: #1a202c;
}

.field-label {
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  color: #4a5568;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.5rem;
}

.field-label + .field-input {
  margin-bottom: 0.25rem;
}

.field-input {
  width: 100%;
  padding: 0.625rem 0.875rem;
  font-size: 1rem;
  font-family: inherit;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  transition: all 0.15s;
  background: white;
  color: #1a202c;
}

.field-input:focus {
  outline: none;
  border-color: var(--color-accent);
  box-shadow: 0 0 0 3px var(--color-accent-light, rgba(66, 153, 225, 0.15));
}

.field-input:disabled {
  background: #f7fafc;
  color: #a0aec0;
  cursor: not-allowed;
}

.field-hint {
  font-size: 0.75rem;
  color: #718096;
  margin: 0 0 1.25rem;
}

.grant-error {
  margin: 1rem 0 0;
  padding: 0.625rem 0.875rem;
  background: #fff5f5;
  border: 1px solid #feb2b2;
  border-radius: 6px;
  color: #742a2a;
  font-size: 0.875rem;
}

.modal-footer {
  display: flex;
  gap: 0.75rem;
  padding: 1.25rem 1.5rem;
  border-top: 1px solid #e2e8f0;
  justify-content: flex-end;
}

.modal-cancel {
  padding: 0.625rem 1.25rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: #4a5568;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s;
  font-family: inherit;
}

.modal-cancel:hover:not(:disabled) {
  background: #f7fafc;
  border-color: #cbd5e0;
}

.modal-cancel:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.modal-submit {
  padding: 0.625rem 1.25rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: white;
  background: var(--color-accent);
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s;
  font-family: inherit;
}

.modal-submit:hover:not(:disabled) {
  filter: brightness(0.95);
  transform: translateY(-1px);
}

.modal-submit:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.15s ease-out;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .modal-content,
.modal-leave-active .modal-content {
  transition: transform 0.15s ease-out;
}

.modal-enter-from .modal-content,
.modal-leave-to .modal-content {
  transform: scale(0.96);
}

.arcs-role {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.125rem 0.5rem;
  border-radius: 10px;
  background: #edf2f7;
  color: #4a5568;
}

.arcs-role.has-access {
  background: #c6f6d5;
  color: #22543d;
}

.arcs-draft {
  margin-left: 0.5rem;
  font-size: 0.7rem;
  font-weight: 600;
  padding: 0.125rem 0.4rem;
  border-radius: 4px;
  background: #edf2f7;
  color: #4a5568;
}

.arcs-hint {
  color: #718096;
  font-size: 0.75rem;
  margin-top: 1rem;
}

.more-projects {
  color: #718096;
  font-size: 0.875rem;
  text-align: center;
  margin-top: 1rem;
}

.loading-state,
.error-state {
  text-align: center;
  padding: 4rem 2rem;
}

.spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #e2e8f0;
  border-top-color: var(--color-accent);
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.error-state {
  color: #e53e3e;
}
</style>
